import React from 'react'
import PropTypes from 'prop-types'
import { Button, Select, Spin, Popconfirm, Space, Tag, Collapse, Tooltip } from 'antd'
import { SaveOutlined, EyeOutlined, UndoOutlined, DragOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import PlaceholderOverlay from './PlaceholderOverlay'
import PlaceholderDialog from './PlaceholderDialog'
import { getRamsListItems } from '../../HttpRequests/ramsControlCentre'
var placeholderFields = require('./placeholderFields')

var Option = Select.Option
var Panel = Collapse.Panel
var PLACEHOLDER_CATEGORIES = placeholderFields.PLACEHOLDER_CATEGORIES
var PLACEHOLDER_FIELDS = placeholderFields.PLACEHOLDER_FIELDS

var SIDEBAR_WIDTH = 260
var POINTS_TO_MM = 25.4 / 72

export default class PdfPlaceholderEditor extends React.PureComponent {

  static propTypes = {
    pdfData: PropTypes.object,
    pages: PropTypes.array,
    placeholderConfig: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onPreview: PropTypes.func,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
  }

  static defaultProps = {
    pages: [],
    placeholderConfig: null,
    loading: false,
    saving: false,
  }

  constructor(props) {
    super(props)

    var initialPlaceholders = []
    if (props.placeholderConfig && props.placeholderConfig.placeholders) {
      initialPlaceholders = props.placeholderConfig.placeholders.map(function(ph, idx) {
        return Object.assign({}, ph, { id: ph.id || 'ph_' + (idx + 1) })
      })
    }

    this.state = {
      placeholders: initialPlaceholders,
      selectedPlaceholderId: null,
      dialogVisible: false,
      editingPlaceholder: null,
      hasChanges: false,
      pdfAreaWidth: 0,
      numPages: 0,
      pdfLoading: false,
      pageDimensions: {},
    }

    this.pdfAreaRef = React.createRef()
    this.canvasRefs = {}
    this.pageContainerRefs = {}
    this.pdfDoc = null
    this.nextId = this.getNextIdFromPlaceholders(initialPlaceholders)
    this._renderTimeout = null
  }

  componentDidMount() {
    this.updatePdfAreaWidth()
    window.addEventListener('resize', this.handleResize)
    if (this.props.pdfData) {
      this.loadPdf(this.props.pdfData)
    }
    // Load RAMS checkbox fields dynamically
    placeholderFields.loadRamsCheckboxFields(getRamsListItems).catch(function(err) {
      console.error('Failed to load RAMS checkbox fields:', err)
    })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this._renderTimeout) clearTimeout(this._renderTimeout)
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.placeholderConfig !== this.props.placeholderConfig && this.props.placeholderConfig) {
      var placeholders = []
      if (this.props.placeholderConfig.placeholders) {
        placeholders = this.props.placeholderConfig.placeholders.map(function(ph, idx) {
          return Object.assign({}, ph, { id: ph.id || 'ph_' + (idx + 1) })
        })
      }
      this.setState({ placeholders: placeholders, hasChanges: false })
      this.nextId = this.getNextIdFromPlaceholders(placeholders)
    }

    if (prevProps.pdfData !== this.props.pdfData && this.props.pdfData) {
      this.loadPdf(this.props.pdfData)
    }

    if (prevState.pdfAreaWidth !== this.state.pdfAreaWidth && this.state.numPages > 0) {
      this.renderAllCanvases()
    }
  }

  handleResize = () => {
    this.updatePdfAreaWidth()
  }

  updatePdfAreaWidth = () => {
    if (this.pdfAreaRef.current) {
      this.setState({ pdfAreaWidth: this.pdfAreaRef.current.offsetWidth })
    }
  }

  getNextIdFromPlaceholders = (placeholders) => {
    if (!placeholders || placeholders.length === 0) {
      return 1
    }
    var maxId = 0
    for (var i = 0; i < placeholders.length; i++) {
      var ph = placeholders[i]
      if (ph.id && typeof ph.id === 'string' && ph.id.startsWith('ph_')) {
        var numStr = ph.id.substring(3)
        var num = parseInt(numStr, 10)
        if (!isNaN(num) && num > maxId) {
          maxId = num
        }
      }
    }
    return maxId + 1
  }

  // --- PDF.js loading and canvas rendering ---

  ensurePdfJs = (callback) => {
    if (window.pdfjsLib) {
      callback(window.pdfjsLib)
      return
    }

    // Check if script is already being injected
    var existing = document.getElementById('pdfjs-cdn-script')
    if (existing) {
      existing.addEventListener('load', function() {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'
          callback(window.pdfjsLib)
        }
      })
      return
    }

    var script = document.createElement('script')
    script.id = 'pdfjs-cdn-script'
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js'
    script.onload = function() {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'
        callback(window.pdfjsLib)
      } else {
        console.error('pdf.js failed to initialize')
      }
    }
    script.onerror = function() {
      console.error('Failed to load pdf.js from CDN')
    }
    document.head.appendChild(script)
  }

  loadPdf = (data) => {
    var self = this

    this.setState({ pdfLoading: true, numPages: 0, pageDimensions: {} })

    this.ensurePdfJs(function(pdfjsLib) {
      self._loadPdfWithLib(pdfjsLib, data)
    })
  }

  _loadPdfWithLib = (pdfjsLib, data) => {
    var self = this

    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }

    var pdfSource = { data: new Uint8Array(data) }

    pdfjsLib.getDocument(pdfSource).promise.then(function(pdf) {
      self.pdfDoc = pdf
      var numPages = pdf.numPages
      var promises = []
      for (var i = 1; i <= numPages; i++) {
        promises.push(pdf.getPage(i))
      }

      Promise.all(promises).then(function(pages) {
        var dims = {}
        for (var j = 0; j < pages.length; j++) {
          var viewport = pages[j].getViewport({ scale: 1 })
          dims[j + 1] = {
            ptWidth: viewport.width,
            ptHeight: viewport.height,
            mmWidth: viewport.width * POINTS_TO_MM,
            mmHeight: viewport.height * POINTS_TO_MM,
          }
        }
        self.setState({
          numPages: numPages,
          pageDimensions: dims,
          pdfLoading: false,
        }, function() {
          self._renderTimeout = setTimeout(function() {
            self.renderAllCanvases()
          }, 50)
        })
      })
    }).catch(function(err) {
      console.error('Failed to load PDF:', err)
      self.setState({ pdfLoading: false })
    })
  }

  renderAllCanvases = () => {
    if (!this.pdfDoc) return
    for (var i = 1; i <= this.state.numPages; i++) {
      this.renderPageCanvas(i)
    }
  }

  renderPageCanvas = (pageNum) => {
    var self = this
    var canvas = this.canvasRefs[pageNum]
    if (!canvas || !this.pdfDoc) return

    this.pdfDoc.getPage(pageNum).then(function(page) {
      var dims = self.state.pageDimensions[pageNum]
      if (!dims) return

      var pdfAreaWidth = self.state.pdfAreaWidth || 800
      var containerWidth = pdfAreaWidth - 24
      var renderScale = containerWidth / dims.ptWidth

      var viewport = page.getViewport({ scale: renderScale })

      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.style.width = viewport.width + 'px'
      canvas.style.height = viewport.height + 'px'

      var context = canvas.getContext('2d')
      context.clearRect(0, 0, canvas.width, canvas.height)

      page.render({
        canvasContext: context,
        viewport: viewport,
      })
    })
  }

  getScaleForPage = (pageNum) => {
    var dims = this.state.pageDimensions[pageNum]
    if (!dims) return 1
    var pdfAreaWidth = this.state.pdfAreaWidth || 800
    var containerWidth = pdfAreaWidth - 24
    return containerWidth / dims.mmWidth
  }

  getCanvasSizeForPage = (pageNum) => {
    var dims = this.state.pageDimensions[pageNum]
    if (!dims) return { width: 0, height: 0 }
    var pdfAreaWidth = this.state.pdfAreaWidth || 800
    var containerWidth = pdfAreaWidth - 24
    var renderScale = containerWidth / dims.ptWidth
    return {
      width: dims.ptWidth * renderScale,
      height: dims.ptHeight * renderScale,
    }
  }

  // --- Drag and drop from sidebar ---

  handleFieldDragStart = (e, fieldValue) => {
    e.dataTransfer.setData('text/plain', fieldValue)
    e.dataTransfer.effectAllowed = 'copy'
  }

  handlePdfDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  handlePdfDrop = (e, pageNum) => {
    e.preventDefault()
    e.stopPropagation()
    var fieldValue = e.dataTransfer.getData('text/plain')
    if (!fieldValue) return

    var fieldDef = placeholderFields.getFieldDefinition(fieldValue)
    if (!fieldDef) return

    var container = this.pageContainerRefs[pageNum]
    var rect = container ? container.getBoundingClientRect() : e.currentTarget.getBoundingClientRect()
    var scale = this.getScaleForPage(pageNum)
    var x = Math.max(0, Math.round((e.clientX - rect.left) / scale))
    var y = Math.max(0, Math.round((e.clientY - rect.top) / scale))

    var isImage = fieldDef.type === 'image'
    var isList = fieldDef.type === 'list'
    var defaults = isImage
      ? placeholderFields.DEFAULT_SIGNATURE_PLACEHOLDER
      : isList
        ? placeholderFields.DEFAULT_LIST_PLACEHOLDER
        : placeholderFields.DEFAULT_PLACEHOLDER

    var newPh = Object.assign({}, defaults, {
      id: 'ph_' + this.nextId++,
      field: fieldDef.value,
      type: fieldDef.type,
      category: fieldDef.category,
      page: pageNum,
      x: x,
      y: y,
    })

    var placeholders = this.state.placeholders.slice()
    placeholders.push(newPh)
    this.setState({ placeholders: placeholders, hasChanges: true })
  }

  // --- Click to add ---

  handlePageClick = (e, pageNum) => {
    // Ignore if clicking on a placeholder overlay
    if (e.target !== e.currentTarget) {
      var node = e.target
      while (node && node !== e.currentTarget) {
        if (node.getAttribute && node.getAttribute('data-placeholder')) return
        if (node.tagName === 'CANVAS') break
        node = node.parentNode
      }
    }

    var container = this.pageContainerRefs[pageNum]
    var rect = container ? container.getBoundingClientRect() : e.currentTarget.getBoundingClientRect()
    var scale = this.getScaleForPage(pageNum)
    var x = Math.round((e.clientX - rect.left) / scale)
    var y = Math.round((e.clientY - rect.top) / scale)

    this.setState({
      dialogVisible: true,
      editingPlaceholder: {
        id: null,
        x: x,
        y: y,
        page: pageNum,
      },
      selectedPlaceholderId: null,
    })
  }

  handleDialogSave = (config) => {
    var editing = this.state.editingPlaceholder
    var placeholders = this.state.placeholders.slice()

    if (editing && editing.id) {
      for (var i = 0; i < placeholders.length; i++) {
        if (placeholders[i].id === editing.id) {
          placeholders[i] = Object.assign({}, placeholders[i], config)
          break
        }
      }
    } else {
      var newPh = Object.assign({}, config, {
        id: 'ph_' + this.nextId++,
        page: editing ? editing.page : 1,
        x: editing ? editing.x : 10,
        y: editing ? editing.y : 10,
      })
      placeholders.push(newPh)
    }

    this.setState({
      placeholders: placeholders,
      dialogVisible: false,
      editingPlaceholder: null,
      hasChanges: true,
    })
  }

  handleDialogCancel = () => {
    this.setState({ dialogVisible: false, editingPlaceholder: null })
  }

  handlePlaceholderEdit = (id) => {
    var ph = null
    for (var i = 0; i < this.state.placeholders.length; i++) {
      if (this.state.placeholders[i].id === id) {
        ph = this.state.placeholders[i]
        break
      }
    }
    if (ph) {
      this.setState({ dialogVisible: true, editingPlaceholder: ph })
    }
  }

  handlePlaceholderDelete = (id) => {
    var placeholders = this.state.placeholders.filter(function(ph) {
      return ph.id !== id
    })
    this.setState({ placeholders: placeholders, selectedPlaceholderId: null, hasChanges: true })
  }

  handlePlaceholderMove = (id, position) => {
    var placeholders = this.state.placeholders.map(function(ph) {
      if (ph.id === id) return Object.assign({}, ph, position)
      return ph
    })
    this.setState({ placeholders: placeholders, hasChanges: true })
  }

  handlePlaceholderResize = (id, size) => {
    var placeholders = this.state.placeholders.map(function(ph) {
      if (ph.id === id) return Object.assign({}, ph, size)
      return ph
    })
    this.setState({ placeholders: placeholders, hasChanges: true })
  }

  handlePlaceholderSelect = (id) => {
    this.setState({ selectedPlaceholderId: id })
  }

  handleSave = () => {
    var config = { placeholders: this.state.placeholders }
    this.props.onSave(config)
    this.setState({ hasChanges: false })
  }

  handleReset = () => {
    var initialPlaceholders = []
    if (this.props.placeholderConfig && this.props.placeholderConfig.placeholders) {
      initialPlaceholders = this.props.placeholderConfig.placeholders.map(function(ph, idx) {
        return Object.assign({}, ph, { id: ph.id || 'ph_' + (idx + 1) })
      })
    }
    this.setState({ placeholders: initialPlaceholders, hasChanges: false, selectedPlaceholderId: null })
  }

  handleScrollToPage = (pageNum) => {
    var container = this.pageContainerRefs[pageNum]
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // --- Sidebar: draggable field items ---

  renderFieldsSidebar = () => {
    var self = this
    var placeholders = this.state.placeholders

    var fieldItemStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: '6px 8px',
      marginBottom: 4,
      border: '1px solid #e8e8e8',
      borderRadius: 4,
      cursor: 'grab',
      backgroundColor: '#fafafa',
      fontSize: 12,
      transition: 'background-color 0.15s',
    }

    var dragIconStyle = {
      marginRight: 6,
      color: '#bbb',
      fontSize: 12,
    }

    var fieldCounts = {}
    placeholders.forEach(function(ph) {
      fieldCounts[ph.field] = (fieldCounts[ph.field] || 0) + 1
    })

    var defaultKeys = PLACEHOLDER_CATEGORIES.map(function(c) { return c.key })

    return (
      <div style={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        borderRight: '1px solid #e8e8e8',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8e8e8', fontWeight: 600, fontSize: 13, color: '#333' }}>
          Fields
        </div>
        <div style={{ padding: '0 4px', fontSize: 11, color: '#888', marginTop: 6, marginBottom: 4, paddingLeft: 12 }}>
          Drag fields onto the PDF
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 8px' }}>
          <Collapse defaultActiveKey={defaultKeys} bordered={false} size="small"
            style={{ background: 'transparent' }}
          >
            {PLACEHOLDER_CATEGORIES.map(function(cat) {
              var fields = PLACEHOLDER_FIELDS.filter(function(f) { return f.category === cat.key })
              return (
                <Panel header={cat.label} key={cat.key}
                  style={{ marginBottom: 4 }}
                >
                  {fields.map(function(field) {
                    var count = fieldCounts[field.value] || 0
                    var isImage = field.type === 'image'
                    return (
                      <div
                        key={field.value}
                        draggable
                        onDragStart={function(e) { self.handleFieldDragStart(e, field.value) }}
                        style={fieldItemStyle}
                        onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = '#e6f7ff' }}
                        onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = '#fafafa' }}
                      >
                        <DragOutlined style={dragIconStyle} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {field.label}
                        </span>
                        {isImage && <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', marginLeft: 4, marginRight: 0, padding: '0 4px' }}>img</Tag>}
                        {field.type === 'list' && <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', marginLeft: 4, marginRight: 0, padding: '0 4px' }}>list</Tag>}
                        {count > 0 && <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', marginLeft: 4, marginRight: 0, padding: '0 4px' }}>{count}</Tag>}
                      </div>
                    )
                  })}
                </Panel>
              )
            })}
          </Collapse>
        </div>

        {placeholders.length > 0 && (
          <div style={{ borderTop: '1px solid #e8e8e8', padding: '8px', maxHeight: 180, overflowY: 'auto' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#333', marginBottom: 4 }}>Placed ({placeholders.length})</div>
            {placeholders.map(function(ph) {
              var fieldDef = placeholderFields.getFieldDefinition(ph.field)
              var label = fieldDef ? fieldDef.label : ph.field
              return (
                <div key={ph.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '3px 4px',
                  fontSize: 11,
                  borderRadius: 3,
                  backgroundColor: self.state.selectedPlaceholderId === ph.id ? '#e6f7ff' : 'transparent',
                  cursor: 'pointer',
                  marginBottom: 2,
                }}
                  onClick={function() { self.handlePlaceholderSelect(ph.id); self.handleScrollToPage(ph.page) }}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                  <span style={{ color: '#aaa', fontSize: 10, marginRight: 4 }}>p{ph.page}</span>
                  <Tooltip title="Edit">
                    <EditOutlined
                      style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer', marginRight: 4 }}
                      onClick={function(e) { e.stopPropagation(); self.handlePlaceholderEdit(ph.id) }}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <DeleteOutlined
                      style={{ fontSize: 11, color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={function(e) { e.stopPropagation(); self.handlePlaceholderDelete(ph.id) }}
                    />
                  </Tooltip>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // --- Toolbar ---

  renderToolbar = () => {
    var self = this
    var hasChanges = this.state.hasChanges
    var saving = this.props.saving
    var placeholderCount = this.state.placeholders.length
    var numPages = this.state.numPages

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #e8e8e8', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {numPages > 1 && (
            <span>
              <span style={{ marginRight: 4, fontSize: 12 }}>Jump to:</span>
              <Select
                value={undefined}
                onChange={function(v) { self.handleScrollToPage(v) }}
                placeholder="Page"
                style={{ width: 100 }}
                size="small"
              >
                {Array.from({ length: numPages }, function(_, i) { return i + 1 }).map(function(p) {
                  return <Option key={p} value={p}>{'Page ' + p}</Option>
                })}
              </Select>
              <span style={{ marginLeft: 4, color: '#888', fontSize: 11 }}>{numPages + ' pages'}</span>
            </span>
          )}
          {numPages === 1 && (
            <span style={{ color: '#888', fontSize: 11 }}>1 page</span>
          )}
          <Tag color="blue">{placeholderCount + ' field' + (placeholderCount !== 1 ? 's' : '')}</Tag>
          {hasChanges && <Tag color="orange">Unsaved changes</Tag>}
        </div>
        <Space>
          {this.props.onPreview && (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={this.props.onPreview}
              disabled={placeholderCount === 0}
            >
              Preview
            </Button>
          )}
          {hasChanges && (
            <Popconfirm title="Reset all changes?" onConfirm={this.handleReset} okText="Yes" cancelText="No">
              <Button size="small" icon={<UndoOutlined />}>Reset</Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={this.handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            Save Fields
          </Button>
        </Space>
      </div>
    )
  }

  // --- PDF pages rendered as canvases ---

  renderPdfPages = () => {
    var self = this
    var numPages = this.state.numPages

    if (this.state.pdfLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#888' }}>Loading PDF...</div>
          </div>
        </div>
      )
    }

    if (numPages === 0) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#999' }}>
          No PDF loaded
        </div>
      )
    }

    var pages = []
    for (var i = 1; i <= numPages; i++) {
      pages.push(i)
    }

    return pages.map(function(pageNum) {
      var canvasSize = self.getCanvasSizeForPage(pageNum)
      var scale = self.getScaleForPage(pageNum)
      var pagePlaceholders = self.state.placeholders.filter(function(ph) {
        return ph.page === pageNum
      })

      return (
        <div
          key={pageNum}
          ref={function(el) { self.pageContainerRefs[pageNum] = el }}
          style={{
            position: 'relative',
            width: canvasSize.width,
            height: canvasSize.height,
            margin: '0 auto 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            backgroundColor: '#fff',
            cursor: 'crosshair',
          }}
          onClick={function(e) { self.handlePageClick(e, pageNum) }}
          onDragOver={self.handlePdfDragOver}
          onDrop={function(e) { self.handlePdfDrop(e, pageNum) }}
        >
          <canvas
            ref={function(el) { self.canvasRefs[pageNum] = el }}
            style={{ display: 'block' }}
          />

          {pagePlaceholders.map(function(ph) {
            return (
              <PlaceholderOverlay
                key={ph.id}
                placeholder={ph}
                scale={scale}
                selected={self.state.selectedPlaceholderId === ph.id}
                onSelect={self.handlePlaceholderSelect}
                onEdit={self.handlePlaceholderEdit}
                onDelete={self.handlePlaceholderDelete}
                onMove={self.handlePlaceholderMove}
                onResize={self.handlePlaceholderResize}
              />
            )
          })}

          <div style={{
            position: 'absolute',
            bottom: 8,
            right: 12,
            fontSize: 11,
            color: 'rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            Page {pageNum} of {numPages}
          </div>
        </div>
      )
    })
  }

  render() {
    var loading = this.props.loading

    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#888' }}>Loading PDF...</div>
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {this.renderToolbar()}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {this.renderFieldsSidebar()}
          <div
            ref={this.pdfAreaRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 12,
              backgroundColor: '#f0f0f0',
            }}
            onDragOver={this.handlePdfDragOver}
          >
            {this.renderPdfPages()}
          </div>
        </div>

        <PlaceholderDialog
          visible={this.state.dialogVisible}
          placeholder={this.state.editingPlaceholder}
          onSave={this.handleDialogSave}
          onCancel={this.handleDialogCancel}
        />
      </div>
    )
  }
}
